import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardHeader, Button, Input, Select, Textarea } from '@/components/ui';
import {
  ArrowLeft,
  Save,
  Film,
  Calendar,
  Clock,
  Loader2,
} from 'lucide-react';

interface Movie {
  id: string;
  title: string;
  titleTr?: string;
  slug: string;
  overview?: string;
  overviewTr?: string;
  releaseDate?: string;
  runtime?: number;
  rating?: string;
  genres: string[];
  posterUrl?: string;
  backdropUrl?: string;
  trailerUrl?: string;
  status: string;
  isNowPlaying: boolean;
  isComingSoon: boolean;
  tmdbId?: string;
  imdbId?: string;
  rtsId?: string;
}

const ratingOptions = [
  { value: '', label: 'Select rating' },
  { value: 'G', label: 'G' },
  { value: 'PG', label: 'PG' },
  { value: 'PG-13', label: 'PG-13' },
  { value: 'R', label: 'R' },
  { value: 'NC-17', label: 'NC-17' },
  { value: 'NR', label: 'Not Rated' },
];

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

const genreOptions = [
  'Action',
  'Adventure',
  'Animation',
  'Comedy',
  'Crime',
  'Documentary',
  'Drama',
  'Family',
  'Fantasy',
  'History',
  'Horror',
  'Music',
  'Mystery',
  'Romance',
  'Science Fiction',
  'Thriller',
  'War',
  'Western',
];

export function MovieEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === 'new';

  const [formData, setFormData] = useState<Partial<Movie>>({
    title: '',
    titleTr: '',
    slug: '',
    overview: '',
    overviewTr: '',
    releaseDate: '',
    runtime: undefined,
    rating: '',
    genres: [],
    posterUrl: '',
    backdropUrl: '',
    trailerUrl: '',
    status: 'draft',
    isNowPlaying: false,
    isComingSoon: false,
    tmdbId: '',
    imdbId: '',
    rtsId: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch existing movie
  const { data: movie, isLoading: loadingMovie } = useQuery<Movie>({
    queryKey: ['movie', id],
    queryFn: async () => {
      const response = await api.get(`/admin/movies/${id}`);
      return response.data?.data;
    },
    enabled: !isNew && !!id,
  });

  // Populate form when movie loads
  useEffect(() => {
    if (movie) {
      setFormData({
        ...movie,
        releaseDate: movie.releaseDate?.split('T')[0] || '',
      });
    }
  }, [movie]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: Partial<Movie>) => {
      if (isNew) {
        return api.post('/admin/movies', data);
      }
      return api.put(`/admin/movies/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['movies'] });
      queryClient.invalidateQueries({ queryKey: ['movie', id] });
      navigate('/movies');
    },
    onError: (error: Error & { response?: { data?: { errors?: Record<string, string> } } }) => {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    },
  });

  function handleChange(field: keyof Movie, value: unknown) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  }

  function handleGenreToggle(genre: string) {
    setFormData((prev) => ({
      ...prev,
      genres: prev.genres?.includes(genre)
        ? prev.genres.filter((g) => g !== genre)
        : [...(prev.genres ?? []), genre],
    }));
  }

  function generateSlug(title: string) {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    saveMutation.mutate(formData);
  }

  if (loadingMovie) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/movies"
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-midnight-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white">
              {isNew ? 'Add Movie' : `Edit: ${movie?.title}`}
            </h1>
            <p className="text-gray-400 mt-1">
              {isNew ? 'Create a new movie listing' : 'Update movie details'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/movies')}
          >
            Cancel
          </Button>
          <Button type="submit" loading={saveMutation.isPending}>
            <Save className="h-4 w-4 mr-2" />
            {isNew ? 'Create Movie' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <Card padding="lg">
            <CardHeader title="Basic Information" />
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Title (English)"
                  value={formData.title}
                  onChange={(e) => {
                    handleChange('title', e.target.value);
                    if (isNew && !formData.slug) {
                      handleChange('slug', generateSlug(e.target.value));
                    }
                  }}
                  error={errors.title}
                  required
                />
                <Input
                  label="Title (Turkish)"
                  value={formData.titleTr}
                  onChange={(e) => handleChange('titleTr', e.target.value)}
                  placeholder="Optional"
                />
              </div>

              <Input
                label="URL Slug"
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                error={errors.slug}
                helper="Used in the movie URL"
                required
              />

              <Textarea
                label="Overview (English)"
                value={formData.overview}
                onChange={(e) => handleChange('overview', e.target.value)}
                rows={4}
                placeholder="Movie synopsis..."
              />

              <Textarea
                label="Overview (Turkish)"
                value={formData.overviewTr}
                onChange={(e) => handleChange('overviewTr', e.target.value)}
                rows={4}
                placeholder="Optional Turkish translation"
              />
            </div>
          </Card>

          {/* Details */}
          <Card padding="lg">
            <CardHeader title="Movie Details" />
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Release Date"
                  type="date"
                  value={formData.releaseDate}
                  onChange={(e) => handleChange('releaseDate', e.target.value)}
                  icon={<Calendar className="h-4 w-4" />}
                />
                <Input
                  label="Runtime (minutes)"
                  type="number"
                  value={formData.runtime ?? ''}
                  onChange={(e) =>
                    handleChange('runtime', e.target.value ? parseInt(e.target.value) : undefined)
                  }
                  icon={<Clock className="h-4 w-4" />}
                />
                <Select
                  label="MPAA Rating"
                  options={ratingOptions}
                  value={formData.rating}
                  onChange={(e) => handleChange('rating', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Genres
                </label>
                <div className="flex flex-wrap gap-2">
                  {genreOptions.map((genre) => (
                    <button
                      key={genre}
                      type="button"
                      onClick={() => handleGenreToggle(genre)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        formData.genres?.includes(genre)
                          ? 'bg-gold text-midnight'
                          : 'bg-midnight-100 text-gray-400 hover:bg-midnight-100/80'
                      }`}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* Media */}
          <Card padding="lg">
            <CardHeader title="Media" />
            <div className="space-y-4">
              <Input
                label="Poster URL"
                value={formData.posterUrl}
                onChange={(e) => handleChange('posterUrl', e.target.value)}
                placeholder="https://..."
              />
              <Input
                label="Backdrop URL"
                value={formData.backdropUrl}
                onChange={(e) => handleChange('backdropUrl', e.target.value)}
                placeholder="https://..."
              />
              <Input
                label="Trailer URL (YouTube)"
                value={formData.trailerUrl}
                onChange={(e) => handleChange('trailerUrl', e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
              />
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Preview */}
          <Card padding="lg">
            <CardHeader title="Poster Preview" />
            <div className="aspect-[2/3] rounded-lg overflow-hidden bg-midnight-100">
              {formData.posterUrl ? (
                <img
                  src={formData.posterUrl}
                  alt="Poster preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
                  <Film className="h-12 w-12 mb-2" />
                  <span className="text-sm">No poster</span>
                </div>
              )}
            </div>
          </Card>

          {/* Status & Display */}
          <Card padding="lg">
            <CardHeader title="Publishing" />
            <div className="space-y-4">
              <Select
                label="Status"
                options={statusOptions}
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
              />

              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isNowPlaying}
                    onChange={(e) => handleChange('isNowPlaying', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-600 bg-midnight-100 text-gold focus:ring-gold"
                  />
                  <div>
                    <span className="text-white font-medium">Now Playing</span>
                    <p className="text-sm text-gray-400">Show in Now Playing section</p>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isComingSoon}
                    onChange={(e) => handleChange('isComingSoon', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-600 bg-midnight-100 text-gold focus:ring-gold"
                  />
                  <div>
                    <span className="text-white font-medium">Coming Soon</span>
                    <p className="text-sm text-gray-400">Show in Coming Soon section</p>
                  </div>
                </label>
              </div>
            </div>
          </Card>

          {/* External IDs */}
          <Card padding="lg">
            <CardHeader title="External IDs" description="Optional integration references" />
            <div className="space-y-4">
              <Input
                label="IMDB ID"
                value={formData.imdbId}
                onChange={(e) => handleChange('imdbId', e.target.value)}
                placeholder="e.g., tt1234567"
              />
              <Input
                label="TMDB ID"
                value={formData.tmdbId}
                onChange={(e) => handleChange('tmdbId', e.target.value)}
                placeholder="e.g., 123456"
              />
              <Input
                label="RTS ID"
                value={formData.rtsId}
                onChange={(e) => handleChange('rtsId', e.target.value)}
                placeholder="e.g., ABC123"
              />
            </div>
          </Card>
        </div>
      </div>
    </form>
  );
}
